import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { Timesheet } from '@/types/timesheet';

export const BULK_REJECT_MIN_CHARS = 20;
export const BULK_REJECT_MAX_CHARS = 500;

interface BulkRejectDialogProps {
  open: boolean;
  /** Timesheets included in the bulk rejection */
  timesheets: Timesheet[];
  /** Combined hours across the selected timesheets */
  totalHours: number;
  /** Whether the org requires a reason before a rejection can be submitted */
  requiresReason: boolean;
  /** True while the batch is running */
  isRejecting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Bulk reject confirmation dialog. The reason text is held locally so that
 * typing never re-renders the approval grid behind the dialog.
 */
export function BulkRejectDialog({
  open,
  timesheets,
  totalHours,
  requiresReason,
  isRejecting,
  onCancel,
  onConfirm,
}: BulkRejectDialogProps) {
  const [reason, setReason] = useState('');

  // Reset only when the dialog opens so the text is not cleared mid close-animation.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const trimmedReason = reason.trim();
  const meetsMinimum = trimmedReason.length >= BULK_REJECT_MIN_CHARS;
  const charsRemaining = BULK_REJECT_MIN_CHARS - trimmedReason.length;
  const count = timesheets.length;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isRejecting) return;
        if (!nextOpen) onCancel();
      }}
    >
      <AlertDialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <X className="h-5 w-5 text-destructive" />
            Reject {count} Timesheet{count !== 1 ? 's' : ''}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            You are about to reject {count} timesheet{count !== 1 ? 's' : ''}.
            Please provide a reason below.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 my-4 overflow-y-auto min-h-0">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <div className="text-sm text-muted-foreground">Total Timesheets</div>
              <div className="text-2xl font-bold">{count}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Total Hours</div>
              <div className="text-2xl font-bold">{totalHours.toFixed(1)}h</div>
            </div>
          </div>

          {/* Employee List */}
          <div>
            <div className="text-sm font-medium mb-2">Affected Employees:</div>
            <div className="max-h-[150px] overflow-y-auto p-3 bg-muted/30 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {timesheets.map(ts => (
                  <div key={ts.id} className="flex items-center gap-2">
                    <X className="h-3 w-3 text-destructive" />
                    <span>{ts.employeeName}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-400">
              This rejection reason will be sent to all {count} employee{count !== 1 ? 's' : ''}.
            </p>
          </div>

          {/* Rejection Reason */}
          <div className="space-y-2">
            <Textarea
              placeholder="Enter rejection reason..."
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, BULK_REJECT_MAX_CHARS))}
              rows={4}
            />
            <div className="flex items-center justify-between text-xs">
              <span className={(!requiresReason || meetsMinimum) ? 'text-muted-foreground invisible' : 'text-muted-foreground'}>
                {reason.length === 0
                  ? `Minimum ${BULK_REJECT_MIN_CHARS} characters required`
                  : `${charsRemaining} more character${charsRemaining === 1 ? '' : 's'} needed`}
              </span>
              <span className={cn(
                'font-medium',
                reason.length >= BULK_REJECT_MAX_CHARS * 0.9
                  ? 'text-destructive'
                  : (!requiresReason || meetsMinimum)
                    ? 'text-green-600'
                    : 'text-amber-500'
              )}>
                {reason.length} / {BULK_REJECT_MAX_CHARS}
              </span>
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isRejecting} onClick={onCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              // Keep the dialog open while the batch runs; the parent closes it
              // once the batch finishes.
              e.preventDefault();
              onConfirm(trimmedReason);
            }}
            disabled={(requiresReason && !meetsMinimum) || isRejecting}
          >
            {isRejecting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <X className="h-4 w-4 mr-2" />
            )}
            Reject {count} Timesheet{count !== 1 ? 's' : ''}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
